import React, { useMemo, useEffect } from 'react';
import './CloudLayer.css';

// Create a global SVG definition that persists across remounts
const createGlobalCloudDefs = () => {
  // Remove any existing definition first
  const existingDefs = document.getElementById('globalCloudDefs');
  if (existingDefs) {
    existingDefs.remove();
  }

  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('id', 'globalCloudDefs');
  svg.style.position = 'fixed';
  svg.style.top = '0';
  svg.style.left = '0';
  svg.style.width = '0';
  svg.style.height = '0';
  svg.style.zIndex = '-1';
  svg.style.opacity = '0';
  svg.style.pointerEvents = 'none';

  const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
  const clipPath = document.createElementNS('http://www.w3.org/2000/svg', 'clipPath');
  clipPath.setAttribute('id', 'cloudClip');
  clipPath.setAttribute('clipPathUnits', 'objectBoundingBox');

  const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
  path.setAttribute('d', 'M0.05 0.8 C0.05 0.8,0 0.6,0.15 0.5 C0.1 0.2,0.35 0.1,0.5 0.25 C0.65 0.1,0.85 0.25,0.85 0.5 C0.95 0.5,0.95 0.7,0.85 0.8 C0.7 0.9,0.35 0.9,0.05 0.8Z');

  clipPath.appendChild(path);
  defs.appendChild(clipPath);
  svg.appendChild(defs);
  document.body.appendChild(svg);
};

// Cloud component with animation reset capability
const Cloud = React.memo(({ initX, initY, scale, speed, onAnimationEnd }: {
  initX: number;
  initY: number;
  scale: number;
  speed: number;
  onAnimationEnd: () => void;
}) => {
  useEffect(() => {
    const element = document.createElement('div');
    element.style.animationDuration = `${speed}s`;
    element.addEventListener('animationend', onAnimationEnd);
    
    return () => {
      element.removeEventListener('animationend', onAnimationEnd);
    };
  }, [speed, onAnimationEnd]);

  return (
    <div 
      className="cloud"
      style={{
        '--cloud-init-x': initX,
        '--cloud-init-y': initY,
        '--cloud-scale': scale,
        animation: `cloud-float ${speed}s linear`,
        clipPath: 'url(#cloudClip)',
        WebkitClipPath: 'url(#cloudClip)'
      } as React.CSSProperties}
      onAnimationEnd={onAnimationEnd}
    />
  );
});

const CloudLayer = React.memo(() => {
  // Ensure global SVG definition exists
  useEffect(() => {
    createGlobalCloudDefs();
    return () => {
      const defs = document.getElementById('globalCloudDefs');
      if (defs) {
        defs.remove();
      }
    };
  }, []);

  const [clouds, setClouds] = React.useState(() => 
    Array.from({ length: 8 }).map((_, i) => ({
      id: `${i}-${Date.now()}`,
      initX: Math.random() * 100,
      initY: Math.random() * 85 + 5,
      scale: Math.random() * 0.7 + 0.5,
      speed: Math.random() * 10 + 5
    }))
  );

  const handleCloudAnimationEnd = (cloudId: string) => {
    setClouds(prevClouds => {
      const newClouds = prevClouds.filter(cloud => cloud.id !== cloudId);
      const newCloud = {
        id: `${Date.now()}-${Math.random()}`,
        initX: Math.random() * 100,
        initY: Math.random() * 85 + 5,
        scale: Math.random() * 0.7 + 0.5,
        speed: Math.random() * 10 + 5
      };
      return [...newClouds, newCloud];
    });
  };

  return (
    <div className="cloud-layer">
      {clouds.map(cloud => (
        <Cloud
          key={cloud.id}
          initX={cloud.initX}
          initY={cloud.initY}
          scale={cloud.scale}
          speed={cloud.speed}
          onAnimationEnd={() => handleCloudAnimationEnd(cloud.id)}
        />
      ))}
    </div>
  );
});

export default CloudLayer;
